import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "../../hooks/useRouter";
import "./TalentPulse360Admin.css";

// components
import Modal from "../../components/Modal/Modal";
import MasterTable from "../../components/MasterTable/MasterTable";
import SearchBar from "../../components/SearchBar/SearchBar";

// utility
import { useConstants } from "../../constant/constantProvider";
import { MASTER_TABLE_KEYS, ROUTES } from "../../constant/keyConstants";
import { formatFullDateWithTime, formatRelativeTime } from "../../utils/dateUtils";
import {
    downloadTalentPulseAdmin,
    fetchTalentPulseAdminFiles,
    fetchTalentPulseAdminOptions,
    uploadTalentPulseAdminFile,
} from "../../store/apiActions";
import { validateFiles, getUserFriendlyErrorMessage } from "../../utils/validateFiles";
import { sanitizeTextInput } from "../../utils/textUtils";
import { formatFileSize } from "../../utils/fileHelper";
import { handleApiError } from "../../utils/apiHelper";
import { createUploadDropzoneHandlers } from "../../utils/uploadDropzoneUtils";
import DropdownSelector from "../../components/DropdownSelector/DropdownSelector";

// assets
import arrowIcon from "../../assets/new-images/up-arrow-icon.svg";
import closeIcon from "../../assets/new-images/close-icon.svg";
import downloadIcon from "../../assets/new-images/download-icon.svg";
import fileOrangeIcon from "../../assets/new-images/file-orange-icon.svg";
import plusIcon from "../../assets/new-images/plus-icon.svg";

const ADMIN_PAGE_SIZE = MASTER_TABLE_KEYS.DEFAULT_PAGE_SIZE;
const ADMIN_SEARCH_DEBOUNCE_MS = 700;
const UPLOAD_ALLOWED_EXTENSIONS = ["xlsx"];
const UPLOAD_MAX_SIZE_MB = 50;

const getVariantClass = (value) => {
    if (!value) return "";

    return `is-${String(value).trim().toLowerCase().replace(/\s+/g, "-")}`;
};

const getDownloadLabel = (record) => {
    if (!record.canDownload) {
        return record.downloadUnavailableLabel;
    }

    return `${record.downloadPrefix} ${record.fileName}`;
};

const saveBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const TalentPulse360Admin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const constants = useConstants();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [adminOptions, setAdminOptions] = useState({ countries: [], data_types: [] });
    const [tableRecords, setTableRecords] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
    const [selectedUploadDataType, setSelectedUploadDataType] = useState("");
    const [selectedUploadCountry, setSelectedUploadCountry] = useState("");
    const [selectedUploadFile, setSelectedUploadFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [relativeTimeNow, setRelativeTimeNow] = useState(() => new Date());
    const [refreshKey, setRefreshKey] = useState(0);
    const uploadFileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);


    const clearUploadFileSelection = useCallback(() => {
        setSelectedUploadFile(null);
        setIsDragging(false);
        if (uploadFileInputRef.current) {
            uploadFileInputRef.current.value = "";
        }
    }, []);


    const handleFileChange = useCallback(async (file) => {
        if (!file) {
            clearUploadFileSelection();
            return;
        }
        if (file.size > UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            toast.error(`File size exceeds ${UPLOAD_MAX_SIZE_MB} MB. Please choose a smaller file.`);
            clearUploadFileSelection();
            return;
        }
        const { validFiles, invalidFiles } = await validateFiles([file], UPLOAD_ALLOWED_EXTENSIONS);
        if (invalidFiles.length > 0) {
            const reasonMatch = invalidFiles[0].match(/\[([^\]]+)\]/);
            const firstReason = reasonMatch ? reasonMatch[1].split(",")[0].trim() : "";
            toast.error(getUserFriendlyErrorMessage(firstReason));
            clearUploadFileSelection();
            return;
        }
        setSelectedUploadFile(validFiles[0] || null);
    }, [clearUploadFileSelection]);


    const {
        handleRemoveUploadFile,
        handleUploadDragEnter,
        handleUploadDragOver,
        handleUploadDragLeave,
        handleUploadDrop,
    } = createUploadDropzoneHandlers({
        selectedUploadFile,
        clearUploadFileSelection,
        setIsDragging,
        handleFileChange,
    });

    const dataTypeLabelMap = useMemo(() => (
        adminOptions.data_types.reduce((acc, item) => ({
            ...acc,
            [item.value]: item.label,
        }), {})
    ), [adminOptions.data_types]);

    const templateActions = useMemo(() => (
        adminOptions.data_types.map((item) => ({
            image: fileOrangeIcon,
            name: item.label,
            value: item.value,
        }))
    ), [adminOptions.data_types]);

    const countryOptions = useMemo(() => (
        adminOptions.countries.map((item) => ({
            name: item.label,
            value: item.value,
        }))
    ), [adminOptions.countries]);

    const mapApiRecord = useCallback((record) => ({
        id: record.id,
        fileName: record.file_name || "-",
        dataType: dataTypeLabelMap[record.file_type] || record.file_type,
        fileType: record.file_type,
        uploadedBy: record.uploaded_by || "-",
        rows: record.row_count ?? "-",
        status: record.upload_status || "-",
        uploadedAt: record.upload_time,
        canDownload: record.upload_status === "Ingested",
        downloadPrefix: constants.DOWNLOAD_PREFIX,
        downloadUnavailableLabel: constants.DOWNLOAD_UNAVAILABLE,
    }), [
        constants.DOWNLOAD_PREFIX,
        constants.DOWNLOAD_UNAVAILABLE,
        dataTypeLabelMap,
    ]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setCurrentPage(1);
        }, ADMIN_SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        let isActive = true;

        const loadOptions = async () => {
            const response = await dispatch(fetchTalentPulseAdminOptions());

            if (!isActive) return;

            if (response?.data_types) {
                setAdminOptions(response);
                setSelectedUploadDataType((value) => value || response.data_types?.[0]?.value || "");
                setSelectedUploadCountry((value) => value || response.countries?.[0]?.value || "");
            }
        };

        loadOptions();

        return () => {
            isActive = false;
        };
    }, [dispatch]);

    useEffect(() => {
        let isActive = true;

        const loadFiles = async () => {
            setIsTableLoading(true);
            const response = await dispatch(fetchTalentPulseAdminFiles({
                page: currentPage,
                page_size: ADMIN_PAGE_SIZE,
                ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
            }));

            if (!isActive) return;

            if (response?.data) {
                setTableRecords(response.data.map(mapApiRecord));
                setTotalRecords(response.total || 0);
            } else {
                setTableRecords([]);
                setTotalRecords(0);
            }

            setIsTableLoading(false);
        };

        loadFiles();

        return () => {
            isActive = false;
        };
    }, [currentPage, debouncedSearchTerm, dispatch, mapApiRecord, refreshKey]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setRelativeTimeNow(new Date());
        }, 30000);

        return () => clearInterval(intervalId);
    }, []);

    const handleFileDownload = useCallback(async (record) => {
        if (!record.canDownload) return;

        const blob = await dispatch(downloadTalentPulseAdmin({ download_type: "file", file_id: record.id }));
        if (!(blob instanceof Blob)) return;

        saveBlob(blob, record.fileName);
        toast.info(constants.FILE_DOWNLOAD_SUCCESS);
    }, [constants.FILE_DOWNLOAD_SUCCESS, dispatch]);

    const handleTemplateDownload = useCallback(async (templateName) => {
        const blob = await dispatch(downloadTalentPulseAdmin({ download_type: "template", template_name: templateName }));
        if (!(blob instanceof Blob)) return;

        saveBlob(blob, `${templateName}.xlsx`);
        toast.info(constants.FILE_DOWNLOAD_SUCCESS);
    }, [constants.FILE_DOWNLOAD_SUCCESS, dispatch]);

    const resetUploadForm = useCallback(() => {
        clearUploadFileSelection();
        setSelectedUploadCountry("");
        setSelectedUploadDataType(templateActions?.[0]?.value ?? "");
        setIsUploadPanelOpen(false);
    }, [clearUploadFileSelection, templateActions]);

    const handleUploadSubmit = useCallback(async () => {
        if (!selectedUploadDataType || !selectedUploadCountry || !selectedUploadFile) {
            toast.error(constants.ADMIN_UPLOAD_REQUIRED_MESSAGE);
            return;
        }

        const formData = new FormData();
        formData.append("data_type", selectedUploadDataType);
        formData.append("country", selectedUploadCountry);
        formData.append("file", selectedUploadFile);

        setIsUploading(true);
        let response;
        try {
            response = await dispatch(uploadTalentPulseAdminFile(formData));
        } catch (error) {
            setIsUploading(false);
            handleApiError(error, toast, constants.ADMIN_UPLOAD_FAILED);
            return;
        }
        setIsUploading(false);

        if (response?.error) {
            handleApiError(response.error?.originalError || response.error, toast, constants.ADMIN_UPLOAD_FAILED);
            return;
        }

        const uploadStatus = typeof response?.status === "string"
            ? response.status.trim().toLowerCase()
            : "";

        if (uploadStatus === "failed") {
            const errorMessage = response?.file?.error_details?.message?.trim()
                || response?.message?.trim()
                || constants.ADMIN_UPLOAD_FAILED;

            toast.error(errorMessage);
            resetUploadForm();
            setCurrentPage(1);
            setRefreshKey((value) => value + 1);
            return;
        }

        if (uploadStatus !== "success") {
            handleApiError(response, toast, constants.ADMIN_UPLOAD_FAILED);
            return;
        }

        const successMessage = response?.message?.trim()
            || response?.file?.message?.trim()
            || constants.ADMIN_UPLOAD_SUCCESS;

        toast.info(successMessage);
        resetUploadForm();
        setCurrentPage(1);
        setRefreshKey((value) => value + 1);
    }, [
        constants.ADMIN_UPLOAD_FAILED,
        constants.ADMIN_UPLOAD_REQUIRED_MESSAGE,
        constants.ADMIN_UPLOAD_SUCCESS,
        dispatch,
        resetUploadForm,
        selectedUploadCountry,
        selectedUploadDataType,
        selectedUploadFile,
    ]);

    const columns = useMemo(() => [
        {
            key: "fileName",
            label: constants.COLUMN_FILE_NAME,
            className: "file-name-cell",
            render: (record) => (
                <div className="admin-file-cell">
                    <span className="admin-file-icon" aria-hidden="true">
                        <img src={fileOrangeIcon} alt="fileIcon" />
                    </span>
                    <span className="admin-file-name" title={record.fileName}>
                        {record.fileName}
                    </span>
                </div>
            ),
        },
        {
            key: "dataType",
            label: constants.COLUMN_DATA_TYPE,
            render: (record) => (
                <span className="talent-admin-pill talent-admin-pill--data">
                    {record.dataType}
                </span>
            ),
        },
        {
            key: "uploadedBy",
            label: constants.COLUMN_UPLOADED_BY,
        },
        {
            key: "rows",
            label: constants.COLUMN_ROWS,
            className: "numeric-cell",
        },
        {
            key: "status",
            label: constants.COLUMN_STATUS,
            render: (record) => (
                <span className={`talent-admin-pill talent-admin-pill--status ${getVariantClass(record.status)}`}>
                    {record.status}
                </span>
            ),
        },
        {
            key: "uploaded",
            label: constants.COLUMN_UPLOADED,
            render: (record) => (
                <span title={formatFullDateWithTime(record.uploadedAt)}>
                    {formatRelativeTime(record.uploadedAt, relativeTimeNow) || "-"}
                </span>
            ),
        },
        {
            key: "actions",
            label: constants.COLUMN_ACTIONS,
            className: "actions-cell",
            render: (record) => {
                const downloadLabel = getDownloadLabel(record);

                return (
                    <button
                        type="button"
                        className="admin-download-button button-style"
                        title={downloadLabel}
                        aria-label={downloadLabel}
                        onClick={() => handleFileDownload(record)}
                        disabled={!record.canDownload}
                    >
                        {record.canDownload && <img src={downloadIcon} alt="downloadIcon" />}
                    </button>
                );
            },
        },
    ], [constants, handleFileDownload, relativeTimeNow]);

    return (
        <div className="talent-pulse-admin-container">
            <div className="talent-pulse-admin-content">
                <button
                    className="button-style back-icon talent-admin-back-button"
                    onClick={() => navigate(ROUTES.TALENT_PULSE_360)}
                    type="button"
                >
                    <img
                        src={arrowIcon}
                        alt={constants.BACK_ALT_TEXT}
                        className="heading-arrow-icon"
                    />
                </button>
                <div className="talent-admin-header">
                    <div className="talent-admin-heading">
                        <div className="talent-admin-heading-title">{constants.ADMIN_TITLE}</div>
                        <div className="talent-admin-heading-subtitle">{constants.ADMIN_SUBTITLE}</div>
                    </div>
                    <div className="talent-admin-actions">
                        <SearchBar
                            className="talent-admin-search"
                            value={searchTerm}
                            onChange={(val) => setSearchTerm(sanitizeTextInput(val))}
                            placeholder={constants.ADMIN_SEARCH_PLACEHOLDER}
                        />
                        <div className="talent-admin-template-dropdown">
                            <DropdownSelector
                                items={templateActions}
                                selected={null}
                                onChange={handleTemplateDownload}
                                placeholder={{
                                    image: downloadIcon,
                                    name: constants.DOWNLOAD_TEMPLATES,
                                }}
                                showTriggerIcon
                                showActiveIconInTrigger={false}
                                itemConfig={{
                                    getDefaultImage: (item) => item.image,
                                    getLabel: (item) => item.name,
                                    getValue: (item) => item.value,
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            className="button-style primary-button talent-admin-upload-button"
                            onClick={() => {
                                setSelectedUploadDataType(templateActions?.[0]?.value ?? "");
                                setSelectedUploadCountry("");
                                setSelectedUploadFile(null);
                                setIsUploadPanelOpen(true);
                            }}
                        >
                            <img src={plusIcon} alt="plusIcon" />
                            <span>{constants.UPLOAD_FILE}</span>
                        </button>
                    </div>
                </div>
                <MasterTable
                    ariaLabel={constants.UPLOADED_FILES}
                    className="talent-admin-table"
                    columns={columns}
                    currentPage={currentPage}
                    data={tableRecords}
                    emptyMessage={constants.NO_UPLOADED_FILES}
                    isLoading={isTableLoading}
                    onPageChange={setCurrentPage}
                    pageSize={ADMIN_PAGE_SIZE}
                    totalRecords={totalRecords}
                />
            </div>
            {isUploadPanelOpen && (
                <Modal open={true} onClose={resetUploadForm} className="talent-admin-upload-modal">
                    <div className="talent-admin-upload-modal-header">
                        <div>
                            <div className="talent-admin-upload-modal-title">{constants.ADMIN_UPLOAD_TITLE}</div>
                            <div className="talent-admin-upload-modal-subtitle">{constants.ADMIN_UPLOAD_SUBTITLE}</div>
                        </div>
                        <button
                            type="button"
                            className="button-style talent-admin-upload-close"
                            onClick={resetUploadForm}
                            aria-label={constants.CANCEL}
                            disabled={isUploading}
                        >
                            &times;
                        </button>
                    </div>
                    <div className="talent-admin-upload-modal-body">
                        <div className="talent-admin-upload-field">
                            <span>{constants.ADMIN_UPLOAD_DATA_TYPE} <span className="talent-admin-required">*</span></span>
                            <div className="talent-admin-data-type-grid">
                                {templateActions.map((item) => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={`button-style talent-admin-data-type-option ${selectedUploadDataType === item.value ? "is-selected" : ""}`}
                                        onClick={() => setSelectedUploadDataType(item.value)}
                                        disabled={isUploading}
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="talent-admin-upload-field">
                            <span>{constants.ADMIN_UPLOAD_COUNTRY} <span className="talent-admin-required">*</span></span>
                            <DropdownSelector
                                items={countryOptions}
                                selected={selectedUploadCountry}
                                onChange={setSelectedUploadCountry}
                                placeholder={{ name: constants.ADMIN_UPLOAD_SELECT_COUNTRY }}
                                showTriggerIcon={false}
                                isDisabled={isUploading}
                                itemConfig={{
                                    getLabel: (item) => item.name,
                                    getValue: (item) => item.value,
                                }}
                            />
                        </div>
                        <div className="talent-admin-upload-field">
                            <span>{constants.ADMIN_UPLOAD_FILE} <span className="talent-admin-required">*</span></span>
                            <div
                                className={`talent-admin-file-dropzone${isDragging ? " is-dragging" : ""}${selectedUploadFile ? " has-file" : ""}`}
                                onDragEnter={handleUploadDragEnter}
                                onDragOver={handleUploadDragOver}
                                onDragLeave={handleUploadDragLeave}
                                onDrop={handleUploadDrop}
                            >
                                <input
                                    ref={uploadFileInputRef}
                                    id="talent-admin-file-input"
                                    type="file"
                                    accept=".xlsx"
                                    onChange={(event) => handleFileChange(event.target.files?.[0] || null)}
                                    disabled={isUploading || !!selectedUploadFile}
                                />
                                <div className="talent-admin-file-dropzone-content">
                                    {selectedUploadFile && (
                                        <button
                                            type="button"
                                            className={`button-style talent-admin-file-remove-button ${isUploading ? "disabled-button" : ""}`}
                                            onClick={handleRemoveUploadFile}
                                            aria-label={`Remove ${selectedUploadFile.name}`}
                                            title="Remove file"
                                            disabled={isUploading}
                                        >
                                            <img src={closeIcon} alt="closeIcon" />
                                        </button>
                                    )}
                                    <span className="talent-admin-file-upload-icon" aria-hidden="true">
                                        {selectedUploadFile ? <img src={fileOrangeIcon} alt="fileIcon" /> : "+"}
                                    </span>
                                    <div className="talent-admin-dropzone-title" title={selectedUploadFile?.name || undefined}>
                                        {selectedUploadFile ? selectedUploadFile.name : (constants.ADMIN_UPLOAD_DROPZONE_TITLE || "Drop .xlsx file here or click to browse")}
                                    </div>
                                    <div className="talent-admin-dropzone-subtitle">
                                        {selectedUploadFile
                                            ? `Uploaded file size: ${formatFileSize(selectedUploadFile.size)}`
                                            : (constants.ADMIN_UPLOAD_DROPZONE_SUBTITLE || "Accepted format: .xlsx · Max size 50 MB")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="talent-admin-upload-modal-actions">
                        <button
                            type="button"
                            className="button-style primary-grey-button talent-admin-cancel-button"
                            onClick={resetUploadForm}
                            disabled={isUploading}
                        >
                            {constants.CANCEL}
                        </button>
                        <button
                            type="button"
                            className="button-style primary-button talent-admin-submit-button"
                            onClick={handleUploadSubmit}
                            disabled={isUploading || !selectedUploadDataType || !selectedUploadCountry || !selectedUploadFile}
                        >
                            {isUploading ? constants.PROCESSING : constants.UPLOAD_FILE}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default TalentPulse360Admin;
